<template>
  <div>
    <div class="toolbar">
      <div class="seg" role="group" aria-label="มุมมอง">
        <span class="seg-lbl">แสดง</span>
        <button type="button" class="seg-btn" :class="{ active: view === 'statement' }" :aria-pressed="view === 'statement'" @click="view = 'statement'">
          ตามเอกสาร
        </button>
        <button type="button" class="seg-btn" :class="{ active: view === 'rows' }" :aria-pressed="view === 'rows'" @click="view = 'rows'">
          รายรายการ
        </button>
      </div>
      <div class="toolbar-right">
        <strong class="small">{{ summaryText }}</strong>
        <b-button variant="outline-success" size="sm" aria-label="ส่งออก Excel" title="ส่งออก Excel" @click="exportExcel">
          <i class="fas fa-file-download"></i>
        </b-button>
      </div>
    </div>
    <b-alert :show="!!truncated" variant="warning" class="small py-2">{{ truncated }}</b-alert>

    <!-- ตามเอกสาร: one block per statement, its total checked against what was matched -->
    <div v-if="view === 'statement'" class="table-responsive">
      <table class="table b-table table-sm table-hover recon-group-table">
        <thead class="thead-light">
          <tr>
            <th v-for="f in rowFields" :key="f.key" :class="f.thClass">{{ f.label }}</th>
            <th v-if="isHeadCounterOrManager"></th>
          </tr>
        </thead>
        <tbody v-if="!groups.length">
          <tr><td :colspan="colCount" class="text-center text-muted py-3">ไม่มีรายการรับแล้วในช่วงนี้</td></tr>
        </tbody>
        <tbody v-for="g in groups" :key="g.batchId">
          <tr class="recon-group">
            <td :colspan="colCount">
              <div class="recon-group-line">
                <b>เอกสาร {{ g.statementRef || "-" }}</b>
                <span class="recon-src">{{ sourceLabel(g.batchSource) }}</span>
                <span class="text-muted">
                  <template v-if="g.statementDate">วันที่เอกสาร {{ formatDate(g.statementDate) }} · </template>รับเมื่อ {{ formatDateTime(g.reconciledAt) }}
                </span>
                <span class="flex-grow-1"></span>
                <span class="text-muted">
                  <template v-if="g.depositAmount !== null && g.depositAmount !== undefined">ยอดในเอกสาร {{ formatAmount(g.depositAmount) }} · </template>
                  จับคู่ {{ g.rows.length }} {{ unit }} · {{ formatAmount(g.matchedAmount) }}
                </span>
                <span v-if="g.totalMatches === true" class="recon-chip ok"><i class="fas fa-check"></i> ยอดตรงกัน</span>
                <span v-else-if="g.totalMatches === false" class="recon-chip bad">Reconcile ไม่ครบทุกแถว</span>
                <b-button
                  size="sm"
                  variant="link"
                  :aria-expanded="isOpen(g)"
                  @click="toggle(g)"
                >
                  {{ isOpen(g) ? "ซ่อนรายการ" : `แสดง ${g.rows.length} ${unit}` }}
                </b-button>
                <b-button
                  v-if="isHeadCounterOrManager"
                  size="sm"
                  variant="outline-danger"
                  :disabled="undoingBatchId === g.batchId"
                  @click="undo(g.rows[0])"
                >
                  ยกเลิกทั้งเอกสาร
                </b-button>
              </div>
            </td>
          </tr>
          <template v-if="isOpen(g)">
            <tr v-for="r in g.rows" :key="r.claimId">
              <td v-for="f in rowFields" :key="f.key" :class="[f.tdClass, { 'recon-diff': f.key === 'paidAmount' && paidAmountDiffers(r) }]">
                {{ cellText(f.key, r) }}
              </td>
              <td v-if="isHeadCounterOrManager" class="text-right">
                <b-button size="sm" variant="outline-secondary" :disabled="busy(r)" @click="unmatch(r)">ยกเลิกการจับคู่</b-button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- รายรายการ: the flat list -->
    <b-table
      v-else
      hover
      small
      sort-icon-left
      head-variant="light"
      :items="rows"
      :fields="flatFields"
      responsive
      show-empty
      empty-text="ไม่มีรายการรับแล้วในช่วงนี้"
    >
      <template #cell(serviceDate)="row">
        {{ formatDate(row.item.serviceDate) }}
      </template>
      <template #cell(treatmentItems)="row">
        <span style="white-space: normal">{{ row.item.treatmentItems }}</span>
      </template>
      <template #cell(amount)="row">
        {{ formatAmount(row.item.amount) }}
      </template>
      <template #cell(paidAmount)="row">
        <span :class="{ 'recon-diff': paidAmountDiffers(row.item) }">{{ formatAmount(row.item.paidAmount) }}</span>
      </template>
      <template #cell(reconciledAt)="row">
        {{ formatDateTime(row.item.reconciledAt) }}
      </template>
      <template #cell(buttons)="row">
        <div v-if="isHeadCounterOrManager" class="d-flex justify-content-end" style="gap: 6px">
          <b-button variant="outline-secondary" size="sm" :disabled="busy(row.item)" @click="unmatch(row.item)">
            ยกเลิกการจับคู่
          </b-button>
          <b-button variant="outline-danger" size="sm" :disabled="busy(row.item)" @click="undo(row.item)">
            ยกเลิกทั้งเอกสาร
          </b-button>
        </div>
      </template>
    </b-table>
    <p class="small text-muted mb-0">
      ยกเลิกการจับคู่: เฉพาะรายการนั้นกลับไปที่ค้างรับ และบรรทัดในเอกสารไปอยู่ที่รอตรวจสอบเพื่อจับคู่ใหม่ ·
      ยกเลิกทั้งเอกสาร: ทุกรายการในเอกสารกลับไปที่ค้างรับ
    </p>
    <Dialog ref="Dialog" />
    <Loading v-if="isLoading" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Dialog from "@/components/modal/Dialog.vue";
import Loading from "@/components/Loading";
import {
  apiErrorMessage, formatThaiShortDate, undoConfirmMessage, unmatchConfirmMessage,
  listSummaryText, truncationNote, groupPaidByStatement, paidAmountDiffers,
} from "@/utils/reconciliation";
import { exportClaimsToExcel } from "@/utils/reconciliationExport";

const SOURCE_LABEL = { OCR: "อ่านจากภาพ", FILE: "นำเข้าไฟล์", MANUAL: "ทำเครื่องหมายเอง" };

export default {
  name: "PaidTab",
  components: { Dialog, Loading },
  props: {
    payer: { type: String, required: true },
    // from PayerPanel: { dateFrom, dateTo, dateBasis, keyword, nonce }
    filter: { type: Object, required: true },
  },
  data() {
    return {
      isLoading: false,
      undoingBatchId: null,
      unmatchingLineId: null,
      view: "statement",
      // which statements are expanded; the newest one opens by default
      openBatchIds: [],
    };
  },
  computed: {
    ...mapGetters("moduleReconciliation", ["getPaid", "getPaidMeta"]),
    ...mapGetters({ isAccessMenu: "moduleUser/getIsAccessMenu" }),
    ...mapGetters({ branchInfo: "moduleBranch/getBranchInfo", clinicInfo: "moduleBranch/getClinicInfo" }),
    // reconciliation.manage (undo) is MANAGER/HEADCOUNTER only on the backend;
    // COUNTER only has reconciliation.view. Gate the mutate control here so a
    // COUNTER sees a read-only tab instead of a button that 403s.
    isHeadCounterOrManager() {
      return this.isAccessMenu(["MANAGER", "HEADCOUNTER"]);
    },
    isNhso() {
      return this.payer === "NHSO";
    },
    unit() {
      return this.isNhso ? "visit" : "รายการ";
    },
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    // NHSO rows are visits (flat 700, with doctor + treatments); SSO rows are receipt payments.
    // ยอดที่ได้รับ is what the statement line paid, highlighted where it differs from the receipt.
    rowFields() {
      return [
        { key: "serviceDate", label: "วันที่ใช้สิทธิ" },
        { key: "cid", label: "เลขบัตร ปชช." },
        { key: "patientName", label: "ชื่อ-สกุล" },
        ...(this.isNhso ? [ { key: "doctorName", label: "แพทย์" }, { key: "treatmentItems", label: "รายการรักษา", tdClass: "recon-wrap" } ] : []),
        { key: "receiptNo", label: "เลขที่ใบเสร็จ" },
        { key: "amount", label: this.isNhso ? "ยอดเบิก" : "ยอดในใบเสร็จ", thClass: "text-right", tdClass: "text-right" },
        { key: "paidAmount", label: "ยอดที่ได้รับ", thClass: "text-right", tdClass: "text-right" },
      ];
    },
    colCount() {
      return this.rowFields.length + (this.isHeadCounterOrManager ? 1 : 0);
    },
    flatFields() {
      return [
        ...this.rowFields.map((f) => ({ ...f, sortable: f.key !== "treatmentItems" })),
        { key: "statementRef", label: "เลขที่เอกสาร", sortable: true },
        { key: "reconciledAt", label: "วันที่รับ", sortable: true },
        { key: "buttons", label: "", class: "text-center" },
      ];
    },
    rows() {
      const k = (this.filter.keyword || "").toLowerCase();
      return this.getPaid.filter((r) => !k || `${r.cid || ""} ${r.patientName || ""} ${r.receiptNo || ""} ${r.statementRef || ""}`.toLowerCase().includes(k));
    },
    groups() {
      return groupPaidByStatement(this.rows);
    },
    summaryText() {
      return listSummaryText(this.getPaidMeta?.pagination, this.getPaidMeta?.summary, this.unit);
    },
    truncated() {
      return truncationNote(this.getPaidMeta?.pagination);
    },
  },
  watch: {
    payer() {
      this.load();
    },
    "filter.nonce"() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    ...mapActions("moduleReconciliation", ["fetchPaid", "undoBatch", "updateLineStatus"]),
    paidAmountDiffers,
    async load() {
      this.isLoading = true;
      try {
        await this.fetchPaid({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          payer: this.payer,
          dateFrom: this.filter.dateFrom,
          dateTo: this.filter.dateTo,
          dateBasis: this.filter.dateBasis,
        });
        this.openBatchIds = this.groups.length ? [this.groups[0].batchId] : [];
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, "โหลดรายการรับแล้วไม่สำเร็จ กรุณาลองใหม่"));
      } finally {
        this.isLoading = false;
      }
    },
    isOpen(g) {
      return this.openBatchIds.includes(g.batchId);
    },
    toggle(g) {
      this.openBatchIds = this.isOpen(g) ? this.openBatchIds.filter((id) => id !== g.batchId) : [...this.openBatchIds, g.batchId];
    },
    sourceLabel(source) {
      return SOURCE_LABEL[source] || source || "";
    },
    cellText(key, r) {
      if (key === "serviceDate") return this.formatDate(r.serviceDate);
      if (key === "amount") return this.formatAmount(r.amount);
      if (key === "paidAmount") return this.formatAmount(r.paidAmount);
      return r[key] || "";
    },
    busy(row) {
      return this.undoingBatchId === row.batchId || (!!row.lineId && this.unmatchingLineId === row.lineId);
    },
    // one row only: its claim back to ค้างรับ, its statement line to รอตรวจสอบ
    async unmatch(row) {
      if (!row.lineId) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(unmatchConfirmMessage(row), {
        title: "ยืนยันการยกเลิกการจับคู่", okVariant: "danger", okTitle: "ยกเลิกการจับคู่", cancelTitle: "ปิด",
      });
      if (!confirmed) return;

      this.unmatchingLineId = row.lineId;
      try {
        await this.updateLineStatus({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, lineId: row.lineId, action: "unmatch" });
        this.$refs.Dialog.showToast("success", row.batchSource === "MANUAL" ? "ยกเลิกการรับเงินสำเร็จ" : "ยกเลิกการจับคู่แล้ว บรรทัดในเอกสารอยู่ที่รอตรวจสอบ");
        this.$emit("undone");
        await this.load();
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, "ยกเลิกการจับคู่ไม่สำเร็จ กรุณาลองใหม่"));
      } finally {
        this.unmatchingLineId = null;
      }
    },
    async undo(item) {
      if (!item.batchId) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        undoConfirmMessage(item, this.getPaid),
        { title: "ยืนยันการยกเลิก", okVariant: "danger", okTitle: "ยกเลิกรายการ", cancelTitle: "ปิด" }
      );
      if (!confirmed) return;

      this.undoingBatchId = item.batchId;
      try {
        await this.undoBatch({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          batchId: item.batchId,
        });
        this.$refs.Dialog.showToast("success", "ยกเลิกการรับเงินสำเร็จ");
        this.$emit("undone");
        await this.load();
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, "ยกเลิกการรับเงินไม่สำเร็จ กรุณาลองใหม่"));
      } finally {
        this.undoingBatchId = null;
      }
    },
    exportExcel() {
      exportClaimsToExcel({
        rows: this.rows,
        payer: this.payer,
        status: "paid",
        dateFrom: this.filter.dateFrom,
        dateTo: this.filter.dateTo,
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });
    },
    formatDate(date) {
      return formatThaiShortDate(date);
    },
    formatDateTime(date) {
      return formatThaiShortDate(date, { withTime: true });
    },
    formatAmount(amount) {
      return Number(amount || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.recon-group td {
  background: #f7f9fb;
  border-top: 1px solid #dee2e6;
}
.recon-group-line {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.recon-src {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 999px;
  background: #eef1f4;
  color: #4a5568;
}
.recon-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
}
.recon-chip.ok {
  background: #e3f4e8;
  color: #1e6b33;
}
.recon-chip.bad {
  background: #fdecea;
  color: #a12622;
}
.recon-diff {
  color: #a12622;
  font-weight: 600;
}
.recon-wrap {
  white-space: normal;
}
</style>
